import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers-pro'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'
import startOfDay from 'date-fns/startOfDay'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useBookingContext } from '@providers/booking/booking_context'
import { getDateRange } from '@shared/dates'
import { buildErrorMessage } from '@shared/error/error'
import { buildSearchParamsForTemperTest } from '@shared/header_navigation'
import { isTemperTestEnabled } from 'components-ui/src/shared/features'
import { hasRequestTime } from 'components-ui/src/shared/location'
import { ClientBookingSearchService } from 'service-api/src/services/client_booking_search.service'
import { captureError } from 'service-api/src/shared/monitoring'
import { ProviderClientDto } from 'service-api/src/types/api'
import { DatePickerSkeleton } from './date_picker_skeleton'
import { isDataInCache, isDayAvailable } from './helper'

interface Props {
  startDate: Date
  maxDate: Date
  provider: ProviderClientDto | null
}

export const DatePicker = ({ maxDate, startDate, provider }: Props) => {
  const bookingCtx = useBookingContext()
  const { state: booking } = bookingCtx
  const { accountName = '' } = useParams()
  const isFetching = useRef(false)
  const [isLoading, setIsLoading] = useState(true)
  const [requestDates, setRequestDates] = useState(() => getDateRange(startDate, startDate, maxDate))
  const willUseSlots = isTemperTestEnabled() && hasRequestTime(bookingCtx.state.config.serviceType)

  useEffect(() => {
    if (isFetching.current) {
      return
    }

    if (isDataInCache(requestDates[0], requestDates[1], booking)) {
      setIsLoading(false)
      return
    }

    isFetching.current = true
    const params = buildSearchParamsForTemperTest(bookingCtx, requestDates[0], requestDates[1])

    ClientBookingSearchService.searchByDays(accountName, 'temper-test', params)
      .then(({ results }) => {
        const result = results[0]
        if (!booking.data.temperTestOffer && result) {
          bookingCtx.dispatch({ type: 'SET_TEMPER_TEST_OFFER', payload: { value: result } })
        }
        bookingCtx.dispatch({
          type: 'SET_TEMPER_TEST_AVAILABILITY',
          payload: { availability: result?.availabilityGroupsByDays, startDate: requestDates[0] },
        })
      })
      .catch((error) => {
        enqueueSnackbar(buildErrorMessage(error), { variant: 'error' })
        captureError(error as Error)
      })
      .finally(() => {
        isFetching.current = false
        setIsLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountName, requestDates])

  // now that same day is possible, we should autoselect that date if possible
  useEffect(() => {
    if (!booking.cart.temperTest?.date && isDayAvailable(startDate, booking, provider, willUseSlots)) {
      const data = booking.data.temperTestAvailability?.find(({ id }) => id === startDate.valueOf())
      bookingCtx.dispatch({
        type: 'SET_TEMPER_TEST_DATE',
        payload: {
          groupIdx: 0,
          value: startDate,
          price: data?.availabilityGroups[0]?.availabilities?.[0]?.price?.value,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.data.temperTestAvailability, provider, startDate, willUseSlots])

  const shouldDisableDate = useCallback(
    (day: Date) => {
      if (day < startDate || day > maxDate) {
        return true
      }
      return !isDayAvailable(day, booking, provider, willUseSlots)
    },
    [booking, maxDate, provider, startDate, willUseSlots]
  )

  const onChangeDateValue = useCallback(
    (selectedDate: Date | null) => {
      if (!selectedDate) {
        return
      }
      const data = booking.data.temperTestAvailability?.find(({ id }) => id === startOfDay(selectedDate).valueOf())
      bookingCtx.dispatch({
        type: 'SET_TEMPER_TEST_DATE',
        payload: {
          groupIdx: 0,
          value: selectedDate,
          price: data?.availabilityGroups[0]?.availabilities?.[0]?.price?.value,
        },
      })
    },
    [booking.data.temperTestAvailability, bookingCtx]
  )

  const handleMonthChange = (date: Date) => {
    const range = getDateRange(date, startDate, maxDate)
    setRequestDates(range)
    if (!isDataInCache(range[0], range[1], booking)) {
      setIsLoading(true)
    }
  }

  const monthButtons = isLoading ? { LeftArrowButton: () => <></>, RightArrowButton: () => <></> } : {}

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StaticDatePicker
        displayStaticWrapperAs="desktop"
        showToolbar={false}
        value={booking.cart.temperTest?.date}
        minDate={startDate}
        maxDate={maxDate}
        shouldDisableDate={shouldDisableDate}
        onChange={onChangeDateValue}
        renderInput={(_props) => <></>}
        views={['day']}
        loading={isLoading}
        renderLoading={() => <DatePickerSkeleton />}
        onMonthChange={handleMonthChange}
        components={monthButtons}
      />
    </LocalizationProvider>
  )
}
